import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React, { createContext, useMemo } from 'react'
import type { Price as ModelPrice } from '@s4l/stayforlong/kernel/Price'
import { PricePerNight } from './PricePerNight'
import { TotalPrice } from './TotalPrice'
import { useS4LContext } from '@s4l/react-context'
import styles from './styles.module.css'
import { cn } from '@s4l/utility-css'
import { Typography } from '@s4l/ds'

export interface IPriceProps {
  price: ModelPrice
  nights?: number
  hideDiscount?: boolean
  showOnlyFeatured?: boolean
  align?: 'start' | 'end'
}

interface PriceContextType {
  price?: ModelPrice
  hideDiscount?: boolean
  showOnlyFeatured?: boolean
  nights?: number
}

export const PriceContext = createContext<PriceContextType>({
  price: undefined,
  hideDiscount: false,
  nights: undefined,
})

const Price = ({
  price,
  nights,
  hideDiscount = false,
  showOnlyFeatured = false,
  align = 'start',
}: IPriceProps) => {
  const { i18n } = useS4LContext()

  const contextValue = useMemo(
    () => ({ price, hideDiscount, nights }),
    [price, hideDiscount, nights]
  )

  const messageId = useMemo(() => {
    const vats = Boolean(price?.hasVatsIncluded?.())
    const tax = Boolean(price?.hasTaxIncluded?.())
    const fees = Boolean(price?.hasFeesIncluded?.())

    if (vats && tax) return 'net.price-total-taxes-included'
    if (fees) return 'global.includes-fees'
    if (!vats && !fees) return 'global.taxes-fees-not-inc'
    return 'global.vat-included'
  }, [price])

  if (!price) {
    return null
  }

  return (
    <PriceContext.Provider value={contextValue}>
      <div
        data-testid="price"
        className={cn(styles.priceLayout, {
          [styles.alignStart]: align === 'start',
          [styles.alignEnd]: align === 'end',
        })}
      >
        {/* // TODO: Add Discount */}
        {/* <div className={styles.discount} /> */}
        <div
          data-testid="price-per-night"
          hidden={showOnlyFeatured && !price.isFeaturedPrice('perNight')}
          className={cn(styles.priceWrapper, {
            [styles.isFeatured]: price.isFeaturedPrice('perNight'),
            [styles.showOnlyFeatured]: showOnlyFeatured,
          })}
        >
          <PricePerNight />
        </div>

        <div
          data-testid="price-total"
          hidden={showOnlyFeatured && !price.isFeaturedPrice('total')}
          className={cn(styles.priceWrapper, {
            [styles.isFeatured]: price.isFeaturedPrice('total'),
            [styles.showOnlyFeatured]: showOnlyFeatured,
          })}
        >
          <TotalPrice />
        </div>

        <div className={styles.vats}>
          <Typography variant="caption" size="m" color="var(--color-neutral-500)">
            {i18n.formatMessage({ id: messageId })}
          </Typography>
        </div>

        {/* // TODO: Add details */}
        {/* <div className={styles.details} /> */}
      </div>
    </PriceContext.Provider>
  )
}

Price.displayName = 'Price'
export default withErrorBoundary(Price)
